/**
 * Calculate Payment Amount Action
 *
 * Synchronous action that calculates the current payment amount
 * and determines if a setup intent (for $0 payments) is required.
 */

import { assign } from "xstate";
import { getPriceFromPricingMap } from "../helper/pricing";
import type { PricingMapType } from "../types";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Calculate Payment Amount Action
 *
 * Calculates the current payment amount based on selected/default HRT
 * and pricing map, then sets flags for payment routing logic.
 *
 * Sets:
 * - currentPrice: The calculated price for the current treatment
 * - requiresSetupIntent: true if price is $0 (needs setup intent instead of payment intent)
 *
 * Example use in machine:
 * ```typescript
 * preparePayment: {
 *   entry: "calculatePaymentAmount",
 *   always: [
 *     { guard: "requiresSetupIntent", target: "creatingSetupIntent" },
 *     { target: "createPaymentIntent" }
 *   ]
 * }
 * ```
 */
export const calculatePaymentAmount: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ context }: any) => {
    // Get HRT (selected or default)
    const selectedHrt = context.selectedHrt as { hrtId: number } | undefined;
    const defaultHrt = context.defaultHrt as { hrtId: number } | undefined;
    const hrt = selectedHrt || defaultHrt;

    // Get pricing map and explicit pricing mode from context (set by PAY_NOW event)
    const pricingMap = context.pricingMap as PricingMapType | undefined;
    const pricingMode = context.pricingMode as "CASH" | "INSURED" | undefined;

    // Calculate price using helper, preferring context.pricingMode over pricingMap mode
    // This avoids race conditions when SET_PRICING_MODE is debounced
    const currentPrice = getPriceFromPricingMap(
      pricingMap,
      hrt?.hrtId,
      pricingMode,
    );

    // Check if this is a "pay later" flow - just record card, don't charge
    const isPayLater = context.paymentRoute === "PAY_LATER";

    return {
      currentPrice,
      amount: currentPrice, // Also store as 'amount' for compatibility
      // Setup intent required for $0 payments OR "pay later" flow (card recording only)
      requiresSetupIntent: currentPrice === 0 || isPayLater,
    };
  }),
  metadata: {
    displayName: "calculatePaymentAmount",
    description:
      "Calculate current payment amount and determine if setup intent is required",
    category: "context",
  },
};
